import { fromJS } from 'immutable'
import {
  POINT_TYPES,
  BOUNDARIES_ZOOM_THRESHOLD,
  ZIPS_LINE_WIDTH_BUMP_ZOOM_THRESHOLD,
  STATES_WITHOUT_COUNTY_LEVELS,
  POINT_LAYERS,
} from './../../../../../constants/map'
import { theme } from './../../theme'
import { capitalizeFirstLetter } from '../../utils'
import { keyBy, mapValues } from 'lodash'
import { alpha } from '@material-ui/core'
const poi_data = require('../../../../../constants/poi_data.json')

// find the common starting digits of all the tract geoids in each POI
// so we can more efficiently filter out tracts that don't belong to the POI
poi_data.sites.forEach(
  site =>
    (site.geoid_starts_with = site.tracts.reduce(
      (acc, geoid) => {
        let str = geoid.toString().slice(0, acc.length)
        while (str !== acc) {
          str = str.slice(0, -1)
          acc = acc.slice(0, -1)
        }
        return acc
      },
      site.tracts[0].toString(),
    )),
)

const DATA_GEO_LAYERS = ['tracts', 'zips', 'counties']
const isDataLayer = l => DATA_GEO_LAYERS.includes(l)

// FIPS code for Puerto Rico, which should be filtered out
const PR_FIPS = 43

// scale AI (indigenous) demo points slightly larger since they're sparse
const xI = 1.2

const POINT_COLOR_MAP = mapValues(
  keyBy(POINT_TYPES, 'id'),
  'color',
)

// determines the layer order on the map
const LAYER_Z_MAP = {
  states_shapes: 40,
  states_lines: 41,
  metros_shapes: 45,
  metros_lines: 46,
  pois_shapes: 47,
  pois_lines: 48,
  counties_shapes: 49,
  zips_shapes: 50,
  tracts_shapes: 51,
  redlining_shapes: 53,
  points: 55, // below lines so outlines aren't obscured
  redlining_lines: 60,
  counties_lines: 61,
  zips_lines: 65, // below tracts so if zip boundaries on, they don't obscure hovered tract
  tracts_lines: 66,
}

const getPoints = (
  {
    POIModeActive,
    activeNorm,
    activePOI,
    activePointLayers,
    activeRegion,
    activeYear,
  },
  suffix,
) => {
  const source = `ddkids_points_${
    POIModeActive ? 'wkkf_' : ''
  }${suffix}_${activeYear}`
  return fromJS({
    id: `demo-points-${suffix}`,
    source,
    'source-layer': 'points',
    type: 'circle',
    layout: {
      visibility: 'visible',
    },
    interactive: false,
    paint: {
      'circle-color': [
        'case',
        ['==', ['get', 'd'], 'ai'],
        POINT_COLOR_MAP['ai'],
        ['==', ['get', 'd'], 'ap'],
        POINT_COLOR_MAP['ap'],
        ['==', ['get', 'd'], 'w'],
        POINT_COLOR_MAP['w'],
        ['==', ['get', 'd'], 'hi'],
        POINT_COLOR_MAP['hi'],
        ['==', ['get', 'd'], 'b'],
        POINT_COLOR_MAP['b'],
        'black',
      ],

      'circle-opacity': 1,
      // If it's 'ai', make larger, else standard size.
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        0,
        ['case', ['==', ['get', 'd'], 'ai'], 0.9 * xI, 0.9],
        7,
        ['case', ['==', ['get', 'd'], 'ai'], 1.1 * xI, 1.1],
        10,
        ['case', ['==', ['get', 'd'], 'ai'], 1.1 * xI, 1.1],
        12,
        ['case', ['==', ['get', 'd'], 'ai'], 1.7 * xI, 1.7],
        14,
        ['case', ['==', ['get', 'd'], 'ai'], 2.2 * xI, 2.2],
      ],
    },
    filter: [
      'case',
      // Filter out dots not belonging to active point layers.
      [
        '!',
        [
          'in',
          ['get', 'd'],
          ['literal', activePointLayers],
        ],
      ],
      false,
      // Filter out dots outside the active metro in metro mode.
      [
        'all',
        ['==', ['string', activeNorm], 'm'],
        [
          'any',
          ['==', ['to-number', activeRegion], 0],
          ['!', ['has', 'm']],
          [
            '!=',
            ['to-number', ['get', 'm']],
            ['to-number', activeRegion],
          ],
        ],
      ],
      false,
      // Filter out dots outside the active state in state mode.
      [
        'all',
        ['==', ['string', activeNorm], 's'],
        ['!=', ['get', 's'], activeRegion],
      ],
      false,
      // Filter out dots outside the active POI in POI mode.
      [
        'all',
        POIModeActive,
        ['!=', ['get', 'p'], activePOI],
      ],
      false,
      true,
    ],
  })
}

const getPointLayers = context =>
  POINT_LAYERS.map(suffix => ({
    z: LAYER_Z_MAP.points,
    style: getPoints(context, suffix),
    idMap: true,
    hasFeatureId: true,
    type: `demo-points-${suffix}`, // `${source}Points`,
  }))

/**
 * Returns mapbox expression to filter
 * a provided type of shape.
 * @param  {String} type Type of shape
 * @param  {object} context Obj carrying state values
 * @return {Array} Mapbox expression
 */
const getShapeFilters = (
  type,
  {
    POIModeActive,
    activeNorm,
    activePOI,
    activeRegion,
    activeGeoType,
    activeRedliningTypes,
    viewport,
  },
) => {
  const activePoi = poi_data.sites.find(
    s => s.id === activePOI,
  )
  if (type === 'tracts') {
    return [
      'case',
      ['!', ['has', 's']],
      false,
      [
        'all',
        POIModeActive,
        [
          'any',
          [
            // first filter by the shared leading digits of all the tract geoids in the POI
            // to avoid comparing each tract in the country to the up to 300 tracts in the POI
            '!=',
            [
              'slice',
              ['to-string', ['id']],
              0,
              activePoi.geoid_starts_with.length,
            ],
            activePoi.geoid_starts_with,
          ],
          [
            // if the geoid does indeed start with the appropriate digits, check the actual list
            '!',
            ['in', ['id'], ['literal', activePoi.tracts]],
          ],
        ],
      ],
      false,
      // Filter out tracts outside the active state in state mode.
      [
        'all',
        ['==', ['string', activeNorm], 's'],
        ['!=', ['get', 's'], activeRegion],
      ],
      false,
      // Filter out tracts outside the active metro in metro mode.
      [
        'all',
        ['==', ['string', activeNorm], 'm'],
        [
          'any',
          // tracts not in a top 100 metro
          ['!', ['has', 'i']],
          ['!=', ['number', ['get', 'i']], 1],
          ['!=', ['get', 'm'], activeRegion],
        ],
      ],
      false,
      ['==', ['number', ['get', 's']], PR_FIPS],
      false,
      true,
    ]
  } else if (type === 'zips') {
    return [
      'case',
      // Filter out zips outside the active state in state mode.
      [
        'all',
        ['==', ['string', activeNorm], 's'],
        ['!=', ['get', 's'], activeRegion],
      ],
      false,
      // Filter out zips outside the active metro area in metro mode.
      [
        'all',
        ['==', ['string', activeNorm], 'm'],
        [
          'any',
          ['!', ['has', 'm']],
          ['!=', ['get', 'm'], activeRegion],
        ],
      ],
      false,
      ['==', ['number', ['get', 's']], PR_FIPS],
      false,
      true,
    ]
  } else if (type === 'counties') {
    return [
      'case',
      // Filter out counties outside the active state in state mode.
      [
        'all',
        ['==', ['string', activeNorm], 's'],
        ['!=', ['get', 's'], activeRegion],
      ],
      false,
      // Counties not available in metro mode.
      ['==', ['string', activeNorm], 'm'],
      false,
      ['==', ['number', ['get', 's']], PR_FIPS],
      false,
      true,
    ]
  } else if (type === 'metros') {
    return [
      'case',
      // Not in top 100, don't display.
      ['!=', ['get', 'i'], 1],
      false,
      ['==', ['number', ['get', 's']], PR_FIPS],
      false,
      true,
    ]
  } else if (type === 'states') {
    return [
      'case',
      [
        'all',
        ['==', ['string', activeGeoType], 'counties'],
        [
          'in',
          ['id'],
          ['literal', STATES_WITHOUT_COUNTY_LEVELS],
        ],
      ],
      false,
      true,
    ]
  } else if (type === 'redlining') {
    return [
      'case',
      // no redlining shapes if zoomed out
      ['<', viewport.zoom, 7],
      false,
      // filter out grades that aren't active
      [
        '!',
        [
          'in',
          ['get', 'holc_grade'],
          ['literal', activeRedliningTypes],
        ],
      ],
      false,
      // no redlining shapes outside the active state in state mode.
      // NOTE: -1 and -2 are used as fallback defaults that won't match any actual values
      [
        'all',
        ['==', ['string', activeNorm], 's'],
        [
          '!=',
          ['to-number', ['get', 'statefips'], -1],
          ['to-number', activeRegion, -2],
        ],
      ],
      false,
      // no redlining shapes outside the active metro area in metro mode.
      [
        'all',
        ['==', ['string', activeNorm], 'm'],
        [
          '!=',
          ['to-number', ['get', 'msaid15'], -1],
          ['to-number', activeRegion, -2],
        ],
      ],
      false,
      // no redlining shapes outside the active POI in POI mode.
      [
        'all',
        POIModeActive,
        [
          '!=',
          ['to-number', ['get', 'msaid15'], -1],
          ['to-number', activePoi.metro_fips, -2],
        ],
      ],
      false,
      true,
    ]
  } else if (type === 'pois') return true
}

const getPolygonLines = (source, type, context) => {
  const { showZipBoundaries, viewport } = context
  return fromJS({
    id: `${type}Lines`,
    source: source,
    'source-layer': type,
    type: 'line',
    layout: {
      visibility: 'visible',
      'line-cap': 'round',
      'line-join': 'round',
      'line-round-limit': 0.2,
    },
    interactive: false,
    paint: {
      'line-color': [
        'case',
        // state/metro borders (metro only visible w/sufficient zoom)
        [
          'any',
          ['==', type, 'states'],
          [
            'all',
            ['==', type, 'metros'],
            [
              '>=',
              viewport.zoom,
              BOUNDARIES_ZOOM_THRESHOLD,
            ],
          ],
          [
            'all',
            ['==', type, 'pois'],
            [
              '>=',
              viewport.zoom,
              BOUNDARIES_ZOOM_THRESHOLD,
            ],
          ],
        ],
        theme.extras.ratings.levelColors[4],
        // hovered/active tracts/zips are red
        [
          'all',
          [
            'any',
            ['==', type, 'tracts'],
            ['==', type, 'zips'],
            ['==', type, 'counties'],
          ],
          [
            'any',
            ['==', ['feature-state', 'hovered'], true],
            ['==', ['feature-state', 'active'], true],
          ],
        ],
        'white',
        // theme.extras.variables.colors.ddkMainBlue,
        // zip boundaries
        [
          'all',
          ['==', type, 'zips'],
          showZipBoundaries,
          ['>=', viewport.zoom, BOUNDARIES_ZOOM_THRESHOLD],
        ],
        theme.extras.variables.colors.zipYellow,
        // redlining boundaries
        ['==', type, 'redlining'],
        [
          'case',
          ['==', ['get', 'holc_grade'], 'A'],
          theme.extras.redlining.A,
          ['==', ['get', 'holc_grade'], 'B'],
          theme.extras.redlining.B,
          ['==', ['get', 'holc_grade'], 'C'],
          theme.extras.redlining.C,
          ['==', ['get', 'holc_grade'], 'D'],
          theme.extras.redlining.D,
          'transparent',
        ],
        'transparent',
      ],
      'line-width':
        type === 'redlining'
          ? [
              'interpolate',
              ['linear'],
              ['zoom'],
              7,
              0.4,
              10,
              1,
              14,
              4,
            ]
          : [
              'case',
              // TRACTS & ZIPS:
              // Selected
              [
                'all',
                [
                  'any',
                  ['==', type, 'tracts'],
                  ['==', type, 'zips'],
                  ['==', type, 'counties'],
                ],
                ['==', ['feature-state', 'active'], true],
              ],
              [
                'case',
                // ['<', viewport.zoom, 5],
                // 1,
                ['<', viewport.zoom, 7],
                1.5,
                ['<', viewport.zoom, 10],
                3,
                ['<', viewport.zoom, 12],
                6,
                9,
              ],
              // Hovered
              [
                'all',
                [
                  'any',
                  ['==', type, 'tracts'],
                  ['==', type, 'zips'],
                  ['==', type, 'counties'],
                ],
                ['==', ['feature-state', 'hovered'], true],
              ],
              [
                'case',
                ['<', viewport.zoom, 7],
                1,
                ['<', viewport.zoom, 10],
                2,
                ['<', viewport.zoom, 12],
                4,
                6,
              ],
              // zips outlines
              ['==', type, 'zips'],
              [
                'case',
                ['<', viewport.zoom, 7],
                0.4,
                ['<', viewport.zoom, 10],
                0.4,
                ['<', viewport.zoom, 12],
                0.6,
                0.6,
              ],
              // STATES & METROS
              ['==', type, 'states'],
              1,
              ['==', type, 'metros'],
              1,
              ['==', type, 'pois'],
              1,
              0,
            ],
      'line-opacity': [
        'case',
        [
          'all',
          ['==', type, 'tracts'],
          // Hovered but not selected.
          ['==', ['feature-state', 'hovered'], true],
          ['==', ['feature-state', 'active'], false],
        ],
        0.75,
        ['==', type, 'redlining'],
        0.8,
        1,
      ],
    },
    filter: getShapeFilters(type, context),
  })
}

const getPolygonShapes = (source, type, context) => {
  const {
    activeMetric,
    activeNorm,
    activeRatingType,
  } = context
  return fromJS({
    id: `${type}Shapes`,
    source: source,
    'source-layer': type,
    type: 'fill',
    layout: {
      visibility: 'visible',
    },
    interactive: isDataLayer(type),
    paint: {
      'fill-color': [
        'case',
        [
          'any',
          ['==', type, 'states'],
          ['==', type, 'metros'],
          ['==', type, 'pois'],
        ],
        'rgba(186, 186, 186, 0.7)',
        [
          'all',
          ['==', ['string', activeRatingType], 's'],
          [
            'any',
            ['==', type, 'tracts'],
            ['==', type, 'zips'],
            ['==', type, 'counties'],
          ],
        ],
        [
          'interpolate',
          ['linear'],
          ['get', 's' + activeMetric + activeNorm],
          0,
          theme.extras.ratings.scoreColors[0],
          14.3,
          theme.extras.ratings.scoreColors[1], // 100 / 7 ≈ 14.3
          28.6,
          theme.extras.ratings.scoreColors[2],
          42.9,
          theme.extras.ratings.scoreColors[3],
          57.1,
          theme.extras.ratings.scoreColors[4],
          71.4,
          theme.extras.ratings.scoreColors[5],
          85.7,
          theme.extras.ratings.scoreColors[6],
          100,
          theme.extras.ratings.scoreColors[7],
        ],
        [
          'all',
          ['==', ['string', activeRatingType], 'x'],
          [
            'any',
            ['==', type, 'tracts'],
            ['==', type, 'zips'],
            ['==', type, 'counties'],
          ],
        ],
        [
          'case',
          [
            '==',
            ['get', 'x' + activeMetric + activeNorm],
            0,
          ],
          theme.extras.ratings.levelColors[0],
          [
            '==',
            ['get', 'x' + activeMetric + activeNorm],
            1,
          ],
          theme.extras.ratings.levelColors[1],
          [
            '==',
            ['get', 'x' + activeMetric + activeNorm],
            2,
          ],
          theme.extras.ratings.levelColors[2],
          [
            '==',
            ['get', 'x' + activeMetric + activeNorm],
            3,
          ],
          theme.extras.ratings.levelColors[3],
          [
            '==',
            ['get', 'x' + activeMetric + activeNorm],
            4,
          ],
          theme.extras.ratings.levelColors[4],
          '#ccc',
        ],
        ['==', type, 'redlining'],
        [
          'case',
          ['==', ['get', 'holc_grade'], 'A'],
          alpha(theme.extras.redlining.A, 0.35),
          ['==', ['get', 'holc_grade'], 'B'],
          alpha(theme.extras.redlining.B, 0.35),
          ['==', ['get', 'holc_grade'], 'C'],
          alpha(theme.extras.redlining.C, 0.35),
          ['==', ['get', 'holc_grade'], 'D'],
          alpha(theme.extras.redlining.D, 0.35),
          'transparent',
        ],
        'transparent',
      ],
      'fill-opacity': [
        'case',
        ['==', type, 'redlining'],
        1,
        0.9,
      ],
    },
    filter: getShapeFilters(type, context),
  })
}

const getPolygonLayer = (
  source,
  type,
  context,
  layerType,
) => {
  const getter = {
    shapes: getPolygonShapes,
    lines: getPolygonLines,
  }[layerType]
  return {
    z: LAYER_Z_MAP[`${type}_${layerType}`],
    style: getter(source, type, context),
    idMap: true,
    hasFeatureId: true,
    type: `${type}${capitalizeFirstLetter(layerType)}`,
  }
}

const getPolygonLayers = (source, type, context) => [
  getPolygonLayer(source, type, context, 'shapes'),
  getPolygonLayer(source, type, context, 'lines'),
]

/**
 * @param  {object} context Obj carrying state values
 * @return {Array} of Mapbox Layers
 */
export const getLayers = context => {
  const {
    POIModeActive,
    activeGeoType,
    activeNorm,
    activePointLayers,
    showDensityOverlays,
    showRedlining,
    showZipBoundaries,
  } = context
  const layers = []
  // include States layer iff in state normed mode
  if (activeNorm === 's' && !POIModeActive) {
    layers.push(
      ...getPolygonLayers(
        'ddkids_shapes',
        'states',
        context,
      ),
    )
  }
  // include Metros layer iff in metro normed mode
  if (activeNorm === 'm' && !POIModeActive) {
    layers.push(
      ...getPolygonLayers(
        'ddkids_shapes',
        'metros',
        context,
      ),
    )
  }
  // include POI layer iff in POI mode
  if (POIModeActive) {
    layers.push(
      ...getPolygonLayers(
        'ddkids_poi_shapes',
        'pois',
        context,
      ),
    )
  }
  // include redlining if active
  if (showRedlining) {
    layers.push(
      ...getPolygonLayers(
        'ddkids_redlining',
        'redlining',
        context,
        // 'shapes',
      ),
    )
  }
  // include demo point layers iff demos are active
  if (
    !!showDensityOverlays &&
    activePointLayers.length > 0
  ) {
    layers.push(...getPointLayers(context))
  }
  // include tracts layer iff in tracts data mode
  if (activeGeoType === 'tracts') {
    layers.push(
      ...getPolygonLayers(
        'ddkids_tracts',
        'tracts',
        context,
      ),
    )
  }
  // include counties layer iff in counties data mode
  if (activeGeoType === 'counties') {
    layers.push(
      ...getPolygonLayers(
        'ddkids_counties',
        'counties',
        context,
      ),
    )
  }
  // include zips layer iff in zips data mode
  if (activeGeoType === 'zips') {
    layers.push(
      ...getPolygonLayers('ddkids_zips', 'zips', context),
    )
  } else if (!!showZipBoundaries) {
    // if zip boundaries active but not in zips mode, just need the lines
    layers.push(
      getPolygonLayer(
        'ddkids_zips',
        'zips',
        context,
        'lines',
      ),
    )
  }
  return layers
}
